<template>
  <div>
    <app-form-group
      label="Guest details sent to the hotel"
      label_left
      class="form-group--no-indent"
    >
      <app-switcher
        @change="onReservationPatch"
        v-model="reservation.guest_details"
      />
    </app-form-group>
    <app-cells><h2 class="title">Personal data</h2></app-cells>
    <v-client-table
        :data="table_personal.items"
        :columns="table_personal.columns"
        :options="table_personal.options"
        class="table-default table-default--maxw"
    >
      <template #h__full_name><div class="table-default__left">Full name</div></template>
      <template #count="{ row, index }">{{ index }}</template>
      <template #full_name="{ row }">
        <div class="table-default__left">
          <button
              @click="onCopyToClipboard(row.full_name)"
              class="table-default__copy"
              :class="{'table-default__copy--disabled': !row.full_name}"
          >
            {{ row.full_name }}
          </button>
        </div>
      </template>
      <template #dob="{ row }">
        <button
            @click="onCopyToClipboard(dateFormat(row.dob))"
            class="table-default__copy"
            :class="{'table-default__copy--disabled': !row.dob}"
        >
          {{ dateFormat(row.dob) }}
        </button>
      </template>
      <template #age_type="{ row }">
        <div class="table-default__flex">
          <img v-if="row.age_type" :src="require(`@/assets/img/${row.age_type}-icon.svg`)" alt="">
          <img v-else src="@/assets/img/age-null-icon.svg" alt="">
          <button
              v-if="row.age"
              @click="onCopyToClipboard(row.age)"
              class="table-default__copy"
              :class="{'table-default__copy--disabled': !row.age}"
          >
            {{ row.age }}
          </button>
        </div>
      </template>
      <template #passport="{ row }">
        <button
            v-if="row.passport"
            @click="onCopyToClipboard(row.passport)"
            class="table-default__copy"
            :class="{'table-default__copy--disabled': !row.passport}"
        >
          {{ row.passport }}
        </button>
        <template v-else>-</template>
      </template>
      <template #citizenship="{ row }">
        <app-cells :indent="false" gap="8px" position="center">
          <span :class="`fflag ff-md fflag-${row.citizenship ? row.citizenship.code : 'empty'}`"></span>
          <span>{{ row.citizenship ? row.citizenship.name : 'Not provided' }}</span>
        </app-cells>
      </template>
      <template #food_preference="{ row }">
        <button
            v-if="row.food_preference"
            @click="onCopyToClipboard(row.food_preference.name)"
            class="table-default__copy"
            :class="{'table-default__copy--disabled': !row.food_preference}"
        >
          {{ row.food_preference.name }}
        </button>
        <template v-else>-</template>
        <div v-if="row.food_gluten_free || row.food_lactose_free || row.food_halal || row.food_no_chicken || row.food_no_red_meat" class="table-default__controls">
          <div v-if="row.food_gluten_free" class="status-tag" :style="{ 'backgroundColor': '#a865eb' }">Gluten-free</div>
          <div v-if="row.food_lactose_free" class="status-tag" :style="{ 'backgroundColor': '#5e99e6' }">Lactose-free</div>
          <div v-if="row.food_halal" class="status-tag" :style="{ 'backgroundColor': '#4cb184' }">Halal</div>
          <div v-if="row.food_no_chicken" class="status-tag" :style="{ 'backgroundColor': '#e6b85e' }">No chicken</div>
          <div v-if="row.food_no_red_meat" class="status-tag" :style="{ 'backgroundColor': '#e65e81' }">No red meat</div>
        </div>
      </template>
      <template #allergy="{ row }">{{ row.allergy || '-' }}</template>
    </v-client-table>
    <app-cells><h2 class="title">Arrival details to Tanzania</h2></app-cells>
    <v-client-table
        :data="table_arrival.items"
        :columns="table_arrival.columns"
        :options="table_arrival.options"
        class="table-default table-default--maxw"
    >
      <template #h__full_name>
        <div class="table-default__left">Full name</div>
      </template>
      <template #full_name="{ row }">
        <div class="table-default__left">
          <router-link :to="{name: 'clients-detail', params: {id: row.id }}" class="table-default__link-border">
            {{ row.full_name }}
          </router-link>
        </div>
      </template>
      <template #arrival_method="{ row }">
        <div class="table-default__controls">
          <span
              class="table-default__transport table-default__transport--arrival"
              :class="{
            'table-default__transport--present': row.transport.arrival?.arrival_method,
            'table-default__transport--confirmed': row.transport.arrival?.arrival_confirmed,
            'table-default__transport--car': row.transport.arrival?.arrival_method?.code === 'car',
            'table-default__transport--bus': row.transport.arrival?.arrival_method?.code === 'bus',
            'table-default__transport--self': row.transport.arrival?.arrival_method?.code === 'self'
          }"
          ></span>
        </div>
      </template>
      <template #arrival_date="{ row }">
        {{ row.transport.arrival?.arrival_date ? dateFormat(row.transport.arrival.arrival_date) : '-' }}
        {{ row.transport.arrival?.arrival_time ? row.transport.arrival.arrival_time : '' }}
      </template>
      <template #arrival_company="{ row }">
        {{ row.transport.arrival?.arrival_company ? row.transport.arrival.arrival_company.name : '-' }}
      </template>
      <template #arrival_flight_number="{ row }">
        <span
            v-if="row.transport.arrival?.arrival_flight_number"
            class="status-tag"
            :style="{ 'backgroundColor': row.transport.arrival.arrival_flight_domestic ? '#77b017' : '#5f5f5f' }"
        >
          {{ row.transport.arrival.arrival_flight_number }}
        </span>
        <span v-else>-</span>
      </template>
      <template #arrival_location="{ row }">
        <span v-tooltip="{ content: row.transport.arrival?.arrival_location ? row.transport.arrival.arrival_location.name : '', placement: 'top-start', offset: 10, container: false }">
          {{ row.transport.arrival?.arrival_location ? row.transport.arrival.arrival_location.short_name : '-' }}
        </span>
      </template>
      <template #arrival_notes="{ row }">
        {{ row.transport.arrival?.arrival_notes ? row.transport.arrival.arrival_notes : '-' }}
      </template>
    </v-client-table>
    <app-cells><h2 class="title">Departure details from Tanzania</h2></app-cells>
    <v-client-table
        :data="table_departure.items"
        :columns="table_departure.columns"
        :options="table_departure.options"
        class="table-default table-default--maxw"
    >
      <template #h__full_name>
        <div class="table-default__left">Full name</div>
      </template>
      <template #full_name="{ row }">
        <div class="table-default__left">
          <router-link :to="{name: 'clients-detail', params: {id: row.id }}" class="table-default__link-border">
            {{ row.full_name }}
          </router-link>
        </div>
      </template>
      <template #departure_method="{ row }">
        <div class="table-default__controls">
          <span
              class="table-default__transport table-default__transport--departure"
              :class="{
            'table-default__transport--present': row.transport.departure?.departure_method,
            'table-default__transport--confirmed': row.transport.departure?.departure_confirmed,
            'table-default__transport--car': row.transport.departure?.departure_method?.code === 'car',
            'table-default__transport--bus': row.transport.departure?.departure_method?.code === 'bus',
            'table-default__transport--self': row.transport.departure?.departure_method?.code === 'self'
          }"
          ></span>
        </div>
      </template>
      <template #departure_date="{ row }">
        {{ row.transport.departure?.departure_date ? dateFormat(row.transport.departure.departure_date) : '-' }}
        {{ row.transport.departure?.departure_time ? row.transport.departure.departure_time : '' }}
      </template>
      <template #departure_company="{ row }">
        {{ row.transport.departure?.departure_company ? row.transport.departure.departure_company.name : '-' }}
      </template>
      <template #departure_flight_number="{ row }">
        <span
            v-if="row.transport.departure?.departure_flight_number"
            class="status-tag"
            :style="{ 'backgroundColor': row.transport.departure.departure_flight_domestic ? '#77b017' : '#5f5f5f' }"
        >
          {{ row.transport.departure.departure_flight_number }}
        </span>
        <span v-else>-</span>
      </template>
      <template #departure_location="{ row }">
        <span v-tooltip="{ content: row.transport.departure?.departure_location ? row.transport.departure.departure_location.name : '', placement: 'top-start', offset: 10, container: false }">
          {{ row.transport.departure?.departure_location ? row.transport.departure.departure_location.short_name : '-' }}
        </span>
      </template>
      <template #departure_notes="{ row }">
        {{ row.transport.departure?.departure_notes ? row.transport.departure.departure_notes : '-' }}
      </template>
    </v-client-table>
  </div>
</template>

<script>
import { getReservationsHotelsGuests, patchReservationsHotel } from '@/http'
import { format, parse } from 'date-fns'

export default {
  name: 'HotelsReservationsClients',
  props: {
    reservation: {
      type: Object,
    },
  },
  data() {
    return {
      table_personal: {
        items: [],
        columns: ['count', 'full_name', 'dob', 'age_type', 'passport', 'citizenship', 'food_preference', 'allergy'],
        options: {
          headings: {
            count: '#',
            full_name: 'Full name',
            dob: 'Date of birth',
            age_type: 'Age',
            passport: 'Passport',
            citizenship: 'Citizenship',
            food_preference: 'Diet',
            allergy: 'Allergies',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_arrival: {
        items: [],
        columns: ['full_name', 'arrival_method', 'arrival_date', 'arrival_company', 'arrival_flight_number', 'arrival_location', 'arrival_notes'],
        options: {
          headings: {
            full_name: 'Full name',
            arrival_method: 'Transport mode',
            arrival_date: 'Date',
            arrival_company: 'Company',
            arrival_flight_number: 'Flight number',
            arrival_location: 'Location',
            arrival_notes: 'Notes',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_departure: {
        items: [],
        columns: ['full_name', 'departure_method', 'departure_date', 'departure_company', 'departure_flight_number', 'departure_location', 'departure_notes'],
        options: {
          headings: {
            full_name: 'Full name',
            departure_method: 'Transport mode',
            departure_date: 'Date',
            departure_company: 'Company',
            departure_flight_number: 'Flight number',
            departure_location: 'Location',
            departure_notes: 'Notes',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    onReservationPatch() {
      patchReservationsHotel(this.$route.params.id, { guest_details: this.reservation.guest_details })
        .then(() => {
          this.$emit('get-reservation-data')
        })
    },
    fetchItems() {
      getReservationsHotelsGuests(this.$route.params.id)
        .then(res => {
          this.table_personal.items = res.data
          this.table_arrival.items = res.data
          this.table_departure.items = res.data
        })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    onCopyToClipboard(string) {
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
  }
}
</script>
